/**
 * Lesson package purchase success page (BUT-841 step 10, `desktop-success.png`).
 *
 * Route target for `/lessons/purchased/:userLessonId`. Data is `location.state`
 * only — no holdings endpoint is consumed in this slice, so there is no
 * refetch fallback: building one would mean creating
 * `api/getUserLessons.ts`, which belongs to BUT-842 and would break the
 * rebase contract. On a hard refresh or a pasted URL the state is gone and
 * the member is redirected to the catalogue; the pack is not lost, and
 * BUT-842's holdings page becomes the durable view.
 */

import { Navigate, useLocation } from 'react-router';
import { TenantLink } from '~/lib/navigation';
import { buildUrl, QueryParams } from '~/lib/queryParams';
import { useTenantHandle, useClubId } from '~/services/appRuntime';
import { useClubQuery } from '~/services/internalApi/club';
import { asUtcDate, asTimezone, formatClubDate } from '@butter/library/datetime';
import { formatSubscriptionPrice } from '@butter/ui/domains/subscriptions';
import { CheckmarkIcon } from '@butter/ui/icons';
import { TicketCard, TicketDivider } from '@butter/ui/patterns';
import { LESSON_PACK_STATUS_LABEL, lessonPackCeilingMinutes } from '@butter/library/lessons';
import {
  formatLessonHours,
  type Lesson,
  type PurchasedUserLesson,
  type LessonInvoice,
} from '~/services/internalApi/lessons';
import { LessonPackCard, LessonPageHeader, LessonTicketRow } from './components';

interface LessonPurchaseSuccessState {
  userLesson: PurchasedUserLesson;
  invoice: LessonInvoice | null;
  lesson: Lesson;
}

export function LessonPurchaseSuccessPage() {
  const location = useLocation();
  const [tenantHandle] = useTenantHandle();
  const [clubId] = useClubId();
  const { data: club } = useClubQuery();

  const state = location.state as LessonPurchaseSuccessState | null;
  const catalogueUrl = buildUrl('/lessons', {
    [QueryParams.TENANT_HANDLE]: tenantHandle,
    [QueryParams.CLUB_ID]: clubId,
  });

  if (!state?.userLesson || !state?.lesson) {
    return <Navigate to={catalogueUrl} replace />;
  }

  const { userLesson, invoice, lesson } = state;
  const timezone = club?.timezone ?? asTimezone('UTC');
  const purchaseDateFormatted = formatClubDate(asUtcDate(new Date(userLesson.purchaseDate)), timezone);
  // userLesson.maxTotalMinutes is a per-instance override (NULL = use the
  // template's) — the override-wins rule is now the shared one.
  const includedMinutes = lessonPackCeilingMinutes({ ...userLesson, lesson });
  const hoursLabel = formatLessonHours(includedMinutes);

  // A free pack has `price: null` and `invoice: null` — formatSubscriptionPrice's
  // unknown branch would render "—", so the free case is named explicitly.
  // Free is decided by the missing invoice, not the template price: a member
  // discount can make a priced template free.
  // The INVOICE first: it is what was charged, tax and fees included. The pack
  // row snapshots the pre-tax price, so reading it first under-reports every
  // purchase at a club with a tax rate.
  const isFree = invoice === null;
  const totalPaidText = isFree ? 'Free' : formatSubscriptionPrice(invoice?.amount ?? userLesson.price ?? null).text;

  return (
    <div className="mx-auto max-w-7xl">
      <LessonPageHeader
        title="Your Lesson Package is ready"
        adornment={
          <span aria-hidden className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-butter-greenLight-500 text-white">
            <CheckmarkIcon className="size-6" />
          </span>
        }
      />

      <div className="grid gap-8 lg:grid-cols-2 lg:gap-x-20 lg:items-start">
        <LessonPackCard
          packId={userLesson.id}
          name={lesson.name}
          description={lesson.description}
          colorName={lesson.color}
          statusLabel={LESSON_PACK_STATUS_LABEL[userLesson.status]}
          figureLabel={hoursLabel}
          purchasedLabel={purchaseDateFormatted}
          titleAs="h2"
          data-testid="lesson-success-card"
        />

        <div className="flex flex-col gap-8">
          <TicketCard borderColor="var(--theme-100)">
            <dl className="flex flex-col gap-4 px-7 pt-7">
              <LessonTicketRow label="Purchase Date" value={purchaseDateFormatted} data-testid="lesson-success-purchase-date" />
              <LessonTicketRow label="Lesson Hours" value={hoursLabel} data-testid="lesson-success-hours" />
            </dl>
            <TicketDivider punch className="my-10" />
            {/* No Purchase ID row — Invoice carries only a serial id. */}
            <dl className="px-7 pb-7">
              <LessonTicketRow label="Total Paid" value={totalPaidText} data-testid="lesson-success-total-paid" />
            </dl>
          </TicketCard>

          {/* No Download Receipt yet — no endpoint. */}
          <div className="flex flex-col gap-5 sm:flex-row-reverse">
            <TenantLink
              to="/lessons/book"
              data-testid="lesson-success-book-link"
              className="btn btn-primary h-16 flex-1 px-6 text-xl font-semibold"
            >
              Book a Lesson
            </TenantLink>
            <TenantLink
              to={catalogueUrl}
              data-testid="lesson-success-browse-link"
              className="btn btn-outline h-16 flex-1 border-1.5 px-6 text-xl font-semibold text-fg-quaternary"
            >
              Back to lesson packages
            </TenantLink>
          </div>
        </div>
      </div>
    </div>
  );
}
